import React from 'react';
import PropTypes from 'prop-types';

const getIssueTitle = (issue) => {
	if (issue?.scope === 'project') {
		const projectTitles = {
			labels: 'Project labels',
			labelTypes: 'Project label types',
			notes: 'Project notes',
			slides: 'Project slides',
			verbatims: 'Verbatims list',
		};

		return projectTitles[issue.field] || 'Project data';
	}

	const titleParts = [];

	if (issue?.verbatimId !== undefined) {
		titleParts.push(`Verbatim #${issue.verbatimId}`);
	}

	if (issue?.sourceRow !== undefined) {
		titleParts.push(issue.sourceType === 'row' ? `Row ${issue.sourceRow}` : `Item ${issue.sourceRow}`);
	}

	return titleParts.join(' - ') || 'Imported verbatim';
};

const renderIssue = (issue, index) => (
	<li
		key={`${issue.scope}-${issue.verbatimIndex ?? 'project'}-${issue.field}-${index}`}
		className={`import-validation-item ${issue.severity}`}
	>
		<div className="import-validation-item-header compact">
			<div className="import-validation-item-title compact">
				<strong>{getIssueTitle(issue)}</strong>
			</div>
			<div className="import-validation-item-meta">
				<span className={`import-validation-badge ${issue.severity}`}>{issue.severity}</span>
				{issue.resolution && (
					<span className="import-validation-resolution">{issue.resolution}</span>
				)}
			</div>
		</div>
		<div className="import-validation-message">
			{issue.message || 'The imported data does not match the required structure.'}
		</div>
	</li>
);

const ImportValidationStep = ({ validationReport, activeTab, onTabChange }) => {
	if (!validationReport) {
		return (
			<div className="import-validation-modal">
				<div className="import-validation-empty-state">
					<div className="import-validation-empty-icon">...</div>
					<div>
						<h4>Checking data</h4>
						<p>The imported file is being validated.</p>
					</div>
				</div>
			</div>
		);
	}

	const warnings = validationReport.warnings || [];
	const errors = validationReport.errors || [];
	const verbatimCount = validationReport.verbatimCount || 0;
	const hasValidationIssues = warnings.length > 0 || errors.length > 0;

	if (!hasValidationIssues) {
		return (
			<div className="import-validation-modal">
				<div className="import-validation-empty-state">
					<div className="import-validation-empty-icon">{verbatimCount > 0 ? 'OK' : '0'}</div>
					<div>
						<h4>{verbatimCount > 0 ? 'No anomaly detected' : 'No verbatims detected'}</h4>
						<p>
							{verbatimCount > 0
								? `${verbatimCount} verbatim(s) detected. The imported structure is compatible with the application.`
								: 'The file contains 0 verbatims. You can continue the import.'}
						</p>
					</div>
				</div>
			</div>
		);
	}

	const hasDefectiveVerbatims = errors.length > 0;
	const visibleIssues = activeTab === 'errors' ? errors : warnings;

	return (
		<div className="import-validation-modal">
			<div className="import-validation-summary-banner">
				<div className="import-validation-summary-copy">
					<span className={`import-validation-severity-pill ${hasDefectiveVerbatims ? 'danger' : 'warning'}`}>
						{hasDefectiveVerbatims ? 'Action needed' : 'Review recommended'}
					</span>
					<p>
						{verbatimCount} verbatim(s) detected.{' '}
						{hasDefectiveVerbatims
							? 'You can continue with all verbatims or without the defective verbatims.'
							: 'The corrections listed below were applied automatically.'}
					</p>
				</div>
			</div>

			<div className="import-validation-tabs">
				<button
					type="button"
					className={`import-validation-tab ${activeTab === 'warnings' ? 'active' : ''}`}
					onClick={() => onTabChange('warnings')}
				>
					Warnings <span>{warnings.length}</span>
				</button>
				<button
					type="button"
					className={`import-validation-tab ${activeTab === 'errors' ? 'active' : ''}`}
					onClick={() => onTabChange('errors')}
				>
					Errors <span>{errors.length}</span>
				</button>
			</div>

			<section className={`import-validation-section ${activeTab === 'errors' ? 'error' : 'warning'}`}>
				<ul className="import-validation-list">
					{visibleIssues.map((issue, index) => renderIssue(issue, index))}
				</ul>
			</section>
		</div>
	);
};

ImportValidationStep.propTypes = {
	validationReport: PropTypes.shape({
		warnings: PropTypes.array,
		errors: PropTypes.array,
		verbatimCount: PropTypes.number,
	}),
	activeTab: PropTypes.oneOf(['warnings', 'errors']).isRequired,
	onTabChange: PropTypes.func.isRequired,
};

export default ImportValidationStep;
